import AxiosInstance from '../config/AxiosInstance';

export interface JournalLineInput {
  id?: string;
  chartOfAccountId: string;
  debit: number;
  credit: number;
  description?: string;
}

export interface CreateJournalEntryPayload {
  companyId: string;
  locationId?: string | null;
  transactionMonthsId: string;
  date: string; // YYYY-MM-DD
  entryType?: number;
  comments?: string | null;
  lines: JournalLineInput[];
}

export interface UpdateJournalEntryPayload {
  date?: string;
  locationId?: string | null;
  transactionMonthsId?: string;
  entryType?: number;
  comments?: string | null;
  lines?: JournalLineInput[];
}

export interface JournalEntry {
  id: string;
  internalNumber: number;
  date: string;
  companyId: string;
  locationId?: string | null;
  transactionMonthsId?: string | null;
  approvedById?: string | null;
  approvedAt?: string | null;
  entryType: number;
  status: 'Draft' | 'Completed' | 'Cancelled';
  comments?: string | null;
  isDeleted: boolean;
  createdBy?: string | null;
  createdAt: string;
  updatedBy?: string | null;
  updatedAt: string;
  lines?: JournalLine[];
}

export interface JournalLine {
  id: string;
  journalEntryId: string;
  chartOfAccountId: string;
  systemAccountId?: string | null;
  debit: number;
  credit: number;
  description?: string | null;
  isDeleted: boolean;
  createdBy?: string | null;
  createdAt: string;
  updatedBy?: string | null;
  updatedAt: string;
  chartOfAccount?: {
    id: string;
    accountCode: string;
    accountName: string;
  };
  systemAccount?: {
    id: string;
    systemAccountCode: string;
    systemAccountName: string;
  } | null;
}

const createJournalEntry = async (payload: CreateJournalEntryPayload) => {
  try {
    const { data } = await AxiosInstance.post('/api/finex/journal-entries', payload);
    return { data };
  } catch (error) {
    throw error;
  }
};

const updateJournalEntry = async (id: string, payload: UpdateJournalEntryPayload) => {
  try {
    const { data } = await AxiosInstance.put(`/api/finex/journal-entries/${id}`, payload);
    return { data };
  } catch (error) {
    throw error;
  }
};

const getJournalEntry = async (id: string) => {
  try {
    const { data } = await AxiosInstance.get(`/api/finex/journal-entries/${id}`);
    return { data };
  } catch (error) {
    throw error;
  }
};

export type JournalEntryStatus = 'draft' | 'completed' | 'cancelled';

const getAllJournalEntries = async (
  locationId?: string,
  fromDate?: string,
  toDate?: string,
  status?: 'draft' | 'completed' | 'cancelled',
  search?: string,
  page: number = 1,
  pageSize: number = 50,
  sortBy?: string,
  sortDir?: 'ASC' | 'DESC'
) => {
  try {
    const { data } = await AxiosInstance.get('/api/finex/journal-entries', {
      params: {
        locationId,
        fromDate,
        toDate,
        status,
        search,
        page,
        pageSize,
        sortBy,
        sortDir,
      },
    });
    return { data };
  } catch (error) {
    throw error;
  }
};

const deleteJournalEntry = async (id: string) => {
  try {
    const { data } = await AxiosInstance.delete(`/api/finex/journal-entries/${id}`);
    return { data };
  } catch (error) {
    throw error;
  }
};

const confirmJournalEntry = async (id: string) => {
  try {
    const { data } = await AxiosInstance.post(`/api/finex/journal-entries/${id}/confirm`);
    return { data };
  } catch (error) {
    throw error;
  }
};

const cancelJournalEntry = async (id: string) => {
  try {
    const { data } = await AxiosInstance.post(`/api/finex/journal-entries/${id}/cancel`);
    return { data };
  } catch (error) {
    throw error;
  }
};

const reverseJournalEntry = async (id: string, reverseDate?: string) => {
  try {
    const { data } = await AxiosInstance.post(`/api/finex/journal-entries/${id}/reverse`, null, {
      params: {
        date: reverseDate,
      },
    });
    return { data };
  } catch (error) {
    throw error;
  }
};

export {
  createJournalEntry,
  updateJournalEntry,
  getJournalEntry,
  getAllJournalEntries,
  deleteJournalEntry,
  confirmJournalEntry,
  cancelJournalEntry,
  reverseJournalEntry,
};
